import clsx from 'clsx';
import {
  Children, cloneElement, forwardRef, isValidElement, MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';

import { Text } from '@/components/typography/text';

import { useThrottle } from '../../hooks/useThrottle';
import styles from './dropdownMenu.module.scss';

const VIEWPORT_MARGIN = 8;
const DROPDOWN_MIN_WIDTH = 200;
const DROPDOWN_WIDTH = 150;
const DROPDOWN_OFFSET = 4;
const POSITION_UPDATE_THROTTLE_MS = 16; // ~60fps

interface MenuProps {
  disabled?: boolean;
  value?: string; // The selected value displayed in the button (for 'select' variant)
  icon?: ReactNode; // Icon displayed in the button (for 'menu' variant)
  label?: string; // Label that appears above the dropdown (like Input component)
  children?: ReactNode;
  'data-testid'?: string;
  variant?: 'menu' | 'select'; // 'menu' for action menus (three dots), 'select' for dropdowns
  searchable?: boolean; // Enable search functionality within the dropdown
  className?: string;
}

interface MenuItemProps {
  value: string; // The text value displayed in the menu item
  disabled?: boolean;
  onClick?: ( event: MouseEvent<HTMLButtonElement> ) => void;
  'data-testid'?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  classname?: string;
  description?: string;
}

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ( { children, value, icon, label, disabled = false, 'data-testid': dataTestId, variant = 'select', searchable = false, className }, forwardedRef ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const [ position, setPosition ] = useState( { top: 0, left: 0, width: 0 } );
    const [ searchQuery, setSearchQuery ] = useState( '' );
    const menuRef = useRef<HTMLDivElement>( null );
    const buttonRef = useRef<HTMLButtonElement>( null );
    const searchInputRef = useRef<HTMLInputElement>( null );

    // Close dropdown when clicking outside
    useEffect( () => {
      const handleClickOutside = ( event: globalThis.MouseEvent ) => {
        if (
          menuRef.current &&
          !menuRef.current.contains( event.target as Node ) &&
          buttonRef.current &&
          !buttonRef.current.contains( event.target as Node )
        ) {
          setIsOpen( false );
        }
      };

      if ( isOpen ) {
        document.addEventListener( 'mousedown', handleClickOutside );
        return () => {
          document.removeEventListener( 'mousedown', handleClickOutside );
        };
      }
    }, [ isOpen ] );

    // Close on Escape key
    useEffect( () => {
      const handleEscape = ( event: KeyboardEvent ) => {
        if ( event.key === 'Escape' ) {
          setIsOpen( false );
          buttonRef.current?.focus();
        }
      };

      if ( isOpen ) {
        document.addEventListener( 'keydown', handleEscape );
        return () => {
          document.removeEventListener( 'keydown', handleEscape );
        };
      }
    }, [ isOpen ] );

    const updatePosition = useCallback( () => {
      if ( buttonRef.current ) {
        const rect = buttonRef.current.getBoundingClientRect();
        // Use actual menu height if available, otherwise estimate
        const menuHeight = menuRef.current?.offsetHeight || 300;

        let left = rect.left;
        let top = rect.bottom + DROPDOWN_OFFSET;

        // Keep dropdown away from right edge
        if ( left > window.innerWidth - DROPDOWN_MIN_WIDTH ) {
          left = rect.right - DROPDOWN_WIDTH;
        }

        // Keep dropdown away from left edge
        left = Math.max( VIEWPORT_MARGIN, left );

        // Check if dropdown would go below viewport, if so position it above the button
        if ( top + menuHeight > window.innerHeight - VIEWPORT_MARGIN ) {
          top = rect.top - menuHeight - DROPDOWN_OFFSET;
          // Ensure it doesn't go above viewport
          top = Math.max( VIEWPORT_MARGIN, top );
        }

        setPosition( {
          top: top,
          left: left,
          width: variant === 'menu' ? 0 : rect.width
        } );
      }
    }, [ variant ] );

    // Throttle position updates for better performance during rapid scroll/resize events
    const throttledUpdatePosition = useThrottle( updatePosition, POSITION_UPDATE_THROTTLE_MS );

    const handleToggle = () => {
      if ( !disabled ) {
        if ( !isOpen ) {
          updatePosition();
        }
        setIsOpen( !isOpen );
      }
    };

    const handleItemClick = () => {
      setIsOpen( false );
      setSearchQuery( '' );
    };

    // Reset search and focus input when dropdown opens
    useEffect( () => {
      if ( isOpen ) {
        setSearchQuery( '' );
        if ( searchable && searchInputRef.current ) {
          // Small delay to ensure the input is rendered
          setTimeout( () => {
            searchInputRef.current?.focus();
          }, 0 );
        }
      }
    }, [ isOpen, searchable ] );

    // Update position on scroll or resize, and after menu renders
    useEffect( () => {
      if ( isOpen ) {
        // Update position after a brief delay to ensure menu is rendered
        const timeoutId = setTimeout( () => {
          updatePosition();
        }, 0 );

        // Use capture phase (true) for scroll to catch scroll
        // events on any scrollable ancestor, not just window scroll.
        // This ensures dropdown repositions when scrolling inside containers.
        window.addEventListener( 'scroll', throttledUpdatePosition, true );
        window.addEventListener( 'resize', throttledUpdatePosition );
        return () => {
          clearTimeout( timeoutId );
          window.removeEventListener( 'scroll', throttledUpdatePosition, true );
          window.removeEventListener( 'resize', throttledUpdatePosition );
        };
      }
    }, [ isOpen, updatePosition, throttledUpdatePosition ] );

    // Filter children based on search query
    const childArray = Children.toArray( children );
    const hasDescription = childArray.some( c => isValidElement<MenuItemProps>( c ) && !!c.props.description );

    const filteredChildren = searchable && searchQuery
      ? Children.toArray( children ).filter( ( child ) => {
          if ( isValidElement<MenuItemProps>( child ) ) {
            return child.props.value.toLowerCase().includes( searchQuery.toLowerCase() );
          }
          return false;
        } )
      : children;

    const menuContent = isOpen && (
      <div
        ref={ menuRef }
        className={ clsx( styles.Menu, hasDescription && styles.Menu__withDescriptions ) }
        role="menu"
        style={ {
          position: 'fixed',
          top: `${ position.top }px`,
          left: `${ position.left }px`,
          minWidth: variant === 'menu'
            ? `${ DROPDOWN_MIN_WIDTH }px`
            : position.width > 0 ? `${ position.width }px` : undefined
        } }
      >
        {searchable && (
          <div className={ styles.SearchInputWrapper }>
            <input
              ref={ searchInputRef }
              type="text"
              className={ styles.SearchInput }
              placeholder="Search..."
              value={ searchQuery }
              onChange={ ( e ) => setSearchQuery( e.target.value ) }
              onKeyDown={ ( e ) => {
                // Prevent closing dropdown on Enter
                if ( e.key === 'Enter' ) {
                  e.preventDefault();
                }
                // Stop propagation to prevent Escape from bubbling
                if ( e.key === 'Escape' ) {
                  e.stopPropagation();
                  setSearchQuery( '' );
                }
              } }
            />
          </div>
        )}
        {Children.map( filteredChildren, ( child ) => {
          if ( isValidElement<MenuItemProps>( child ) ) {
            return cloneElement( child, {
              onClick: ( e: MouseEvent<HTMLButtonElement> ) => {
                child.props.onClick?.( e );
                handleItemClick();
              }
            } );
          }
          return child;
        } )}
      </div>
    );

    const buttonId = dataTestId ? `${ dataTestId }-button` : undefined;

    const itemValues = useMemo( () => {
      if ( variant !== 'select' ) return [];
      return Children.toArray( children )
        .filter( ( child ): child is ReactElement<MenuItemProps> => isValidElement<MenuItemProps>( child ) )
        .map( child => child.props.value );
    }, [ children, variant ] );

    return (
      <div className={ clsx( styles.MenuWrapper, className ) } ref={ forwardedRef }>
        {label && (
          <Text as="label" htmlFor={ buttonId } size="sm" weight="normal" className={ styles.label }>
            {label}
          </Text>
        )}
        <button
          ref={ buttonRef }
          id={ buttonId }
          disabled={ disabled }
          className={ variant === 'select' ? styles.SelectButton : styles.RootMenu }
          onClick={ handleToggle }
          data-open={ isOpen ? '' : undefined }
          data-testid={ dataTestId }
          type="button"
          aria-haspopup="true"
          aria-expanded={ isOpen }
        >
          <span className={ styles.ButtonLabel }>
            {variant === 'menu' ? icon : (
              <span className={ styles.ButtonLabelSizer }>
                <span>{value}</span>
                {itemValues.map( ( v, i ) => (
                  <span key={ i } className={ styles.SizerItem } aria-hidden="true">{v}</span>
                ) )}
              </span>
            )}
          </span>
          {variant === 'select' && <FiChevronDown className={ styles.ChevronIcon } />}
        </button>
        {typeof document !== 'undefined' && menuContent && createPortal( menuContent, document.body )}
      </div>
    );
  }
);
Menu.displayName = 'Menu';

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  ( { value, disabled, onClick, 'data-testid': dataTestId, leadingIcon, trailingIcon, classname, description, ...props }, forwardedRef ) => {
    return (
      <button
        { ...props }
        ref={ forwardedRef }
        type="button"
        role="menuitem"
        className={ clsx( styles.MenuItem, classname && classname ) }
        disabled={ disabled }
        onClick={ onClick }
        data-testid={ dataTestId }
      >
        {
          leadingIcon && (
            <span className={ styles.MenuItem_leadingIcon }>{ leadingIcon }</span>
          )
        }
        <span className={ styles.MenuItem_value }>{value}</span>
        {
          trailingIcon && (
            <span className={ styles.MenuItem_trailingIcon }>{ trailingIcon }</span>
          )
        }
        {
          description && (
            <span className={ styles.MenuItem_description }>{ description }</span>
          )
        }
      </button>
    );
  }
);
MenuItem.displayName = 'MenuItem';
